import { api } from "./client";
import type { components } from "./types";

export type Template = components["schemas"]["Template"];
export type TemplateParameter = components["schemas"]["TemplateParameter"];
export type TemplateStatus = NonNullable<Template["status"]>;
export type ValidationReport = components["schemas"]["ValidationReport"];
export type Calendar = components["schemas"]["Calendar"];
export type CreateProjectFromTemplateRequest =
  components["schemas"]["CreateProjectFromTemplateRequest"];
export type ApprovalRecord = components["schemas"]["ApprovalRecord"];
export type AuditEntry = components["schemas"]["AuditEntry"];

/** Shape posted to /api/templates and /api/templates/{pk} (PATCH).
 *
 * `template_id` is optional on POST: backend transliterates from `name` and
 * resolves collisions with a `_N` suffix. PATCH ignores it (server preserves
 * the existing identifier).
 */
export type TemplateDraft = {
  template_id?: string | null;
  name: string;
  description?: string;
  parameters?: Template["parameters"];
  tasks?: Template["tasks"];
};

export function listTemplates(): Promise<Template[]> {
  return api.get<Template[]>("/templates");
}

export function getTemplate(templatePk: number): Promise<Template> {
  return api.get<Template>(`/templates/${templatePk}`);
}

export function listCalendars(): Promise<Calendar[]> {
  return api.get<Calendar[]>("/calendars");
}

export function simulateCreateProjectFromTemplate(
  body: CreateProjectFromTemplateRequest,
): Promise<ApprovalRecord> {
  return api.post<ApprovalRecord>("/simulate/create_project_from_template", body);
}

/** Create a project from a template in one atomic step (simulate + commit). */
export function createProject(body: CreateProjectFromTemplateRequest): Promise<AuditEntry> {
  return api.post<AuditEntry>("/simulate/create_project", body);
}

export function validateTemplate(draft: TemplateDraft): Promise<ValidationReport> {
  return api.post<ValidationReport>("/templates/validate", draft);
}

export function createTemplate(draft: TemplateDraft): Promise<Template> {
  return api.post<Template>("/templates", draft);
}

export function patchTemplate(pk: number, draft: TemplateDraft): Promise<Template> {
  return api.patch<Template>(`/templates/${pk}`, draft);
}

export function deleteTemplate(pk: number): Promise<void> {
  return api.delete<void>(`/templates/${pk}`);
}

export function forkTemplate(pk: number): Promise<Template> {
  return api.post<Template>(`/templates/${pk}/fork`);
}

export function promoteTemplate(pk: number): Promise<Template> {
  return api.post<Template>(`/templates/${pk}/promote`);
}

export function archiveTemplate(pk: number): Promise<Template> {
  return api.post<Template>(`/templates/${pk}/archive`);
}

export function cloneTemplate(
  pk: number,
  body: { template_id: string; name: string },
): Promise<Template> {
  return api.post<Template>(`/templates/${pk}/clone`, body);
}
